import ComponentWithSocket from '../../../lib/component';
import { Component, Watch } from 'vue-property-decorator';
import moment from 'moment';
import 'moment/locale/ro';

import * as Store from '../../../store';
import * as Stock from '../../../utils/stock';
import { depositorName } from '../../../utils/depositor';
import { saveXlsx, XlsxSheet } from '../../../utils/xlsx';

moment.locale('ro');

const SCROLL_KEY = 'stock_list_scroll';

@Component
export default class StockList extends ComponentWithSocket {
    public data: any = {
        lots: [],
        lotsCount: 0,
        counts: {
            total: 0,
            in: 0,
            out: 0,
            uninventoried: 0,
        },
        perPage: 50,
        currentPage: 1,
        stockState: 'all',
        soldFilter: 'all',
        contract: '',
        keyword: '',
        isSearching: false,
        selectedLots: [],
        bulkNote: '',
        bulkOpen: false,
        bulkInStock: true,
        exporting: false,
    };

    get tableFields() {
        return [
            { key: 'select', label: '' },
            { key: 'foto', label: '' },
            { key: 'cod_produs', label: 'Cod' },
            { key: 'lot', label: 'Lot' },
            { key: 'contract', label: 'Contract' },
            { key: 'licitatie', label: 'Licitatie' },
            { key: 'stoc', label: 'Stoc' },
            { key: 'actiuni', label: 'Actiuni' },
        ];
    }

    // Expuse in template pentru ca regulile de afisare sa fie definite o
    // singura data (vezi utils/stock.ts).
    stockLabel = Stock.stockLabel;
    stockBadgeClass = Stock.stockBadgeClass;
    canSetInStock = Stock.canSetInStock;
    canSetOutOfStock = Stock.canSetOutOfStock;
    formatSku = Stock.formatSku;
    formatContractNumber = Stock.formatContractNumber;
    depositorName = depositorName;

    constructor() {
        super();

        this.wsListener.onGet('stock.lots', (success: boolean, data: any) => {
            Store.commitLoaderVisibility(this.$store, false);

            if (!success) {
                return alert(data);
            }

            this.data.lots = data.lots;
            this.data.lotsCount = data.lotsCount;
            this.data.selectedLots = [];
            this.restoreScrollPosition();
        });

        this.wsListener.onGet(
            'stock.lots.counts',
            (success: boolean, data: any) => {
                if (success) {
                    this.data.counts = data;
                }
            },
        );

        this.wsListener.onPut('stock.lot', (success: boolean, data: any) => {
            Store.commitLoaderVisibility(this.$store, false);

            if (!success) {
                return alert(data);
            }

            // Inlocuim doar randul modificat, ca sa nu se piarda pozitia in
            // lista dupa fiecare comutare.
            const index = this.data.lots.findIndex(
                (lot: any) => String(lot._id) === String(data.lot._id),
            );
            if (index !== -1) {
                this.data.lots.splice(index, 1, data.lot);
            }

            this.reloadCounts();
        });

        this.wsListener.onGet(
            'stock.lots.export',
            (success: boolean, data: any) => {
                this.data.exporting = false;
                Store.commitLoaderVisibility(this.$store, false);

                if (!success) {
                    return alert(data);
                }

                this.writeXlsx(data.lots);
            },
        );

        this.wsListener.onPut(
            'stock.lots.bulk',
            (success: boolean, data: any) => {
                if (!success) {
                    Store.commitLoaderVisibility(this.$store, false);
                    return alert(data);
                }

                this.data.bulkOpen = false;
                this.data.bulkNote = '';
                this.reload(this.data.currentPage);
                this.reloadCounts();
            },
        );
    }

    @Watch('data.keyword')
    onKeywordChanged() {
        if (!this.data.isSearching) {
            setTimeout(() => {
                this.data.isSearching = false;
                this.reload(1);
                this.reloadCounts();
            }, 1000);
        }
        this.data.isSearching = true;
    }

    mounted() {
        this.reload(this.data.currentPage);
        this.reloadCounts();
    }

    beforeRouteLeave(to: any, from: any, next: any) {
        sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
        this._beforeLeave(to, from);
        next();
    }

    restoreScrollPosition() {
        const stored = sessionStorage.getItem(SCROLL_KEY);
        if (!stored) return;

        sessionStorage.removeItem(SCROLL_KEY);
        this.$nextTick(() => window.scrollTo(0, parseInt(stored, 10)));
    }

    /** Filtrele trimise catre backend, fara paginare. */
    get filters() {
        const filters: any = {};

        if (this.data.stockState !== 'all') {
            filters.stockState = this.data.stockState;
        }

        if (this.data.soldFilter !== 'all') {
            filters.sold = this.data.soldFilter === 'sold';
        }

        if (this.data.contract) {
            filters.contract = this.data.contract;
        }

        if (this.data.keyword) {
            filters.keyword = this.data.keyword;
        }

        return filters;
    }

    public reload(page: any = this.data.currentPage) {
        Store.commitLoaderVisibility(this.$store, true);
        this.data.currentPage = page;

        this.wsListener.sendGet('stock.lots', {
            ...this.filters,
            perPage: this.data.perPage,
            currentPage: page,
        });
    }

    public reloadCounts() {
        this.wsListener.sendGet('stock.lots.counts', this.filters);
    }

    applyFilters() {
        this.reload(1);
        this.reloadCounts();
    }

    filterByState(state: string) {
        this.data.stockState = state;
        this.applyFilters();
    }

    setStock(lot: any, inStock: boolean) {
        Store.commitLoaderVisibility(this.$store, true);

        this.wsListener.sendPut('stock.lot', {
            _id: lot._id,
            inStock,
        });
    }

    openBulk(inStock: boolean) {
        if (!this.data.selectedLots.length) return;

        this.data.bulkInStock = inStock;
        this.data.bulkNote = '';
        this.data.bulkOpen = true;
    }

    doBulkUpdate() {
        Store.commitLoaderVisibility(this.$store, true);

        this.wsListener.sendPut('stock.lots.bulk', {
            lotIds: this.data.selectedLots.map((lot: any) => lot._id),
            inStock: this.data.bulkInStock,
            note: this.data.bulkNote,
        });
    }

    /** Etichete pentru loturile bifate. */
    printSelectedLabels() {
        if (!this.data.selectedLots.length) return;

        const ids = this.data.selectedLots
            .map((lot: any) => lot._id)
            .join(',');

        this.$router.push(`/etichete?lots=${ids}`);
    }

    printLabel(lot: any) {
        this.$router.push(`/etichete?lots=${lot._id}`);
    }

    openLot(lot: any) {
        this.$router.push(`/stocuri/lot/${lot.sku}`);
    }

    formatDate(date: any): string {
        return date ? moment(date).format('DD.MM.YYYY HH:mm') : '';
    }

    userName(user: any): string {
        if (!user || !user._id) return '';
        return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }

    /**
     * Exportul foloseste aceleasi filtre ca lista, dar fara paginare - de
     * aceea are eveniment propriu, ca raspunsul sa nu inlocuiasca pagina
     * curenta din tabel.
     */
    doExport() {
        this.data.exporting = true;
        Store.commitLoaderVisibility(this.$store, true);

        this.wsListener.sendGet('stock.lots.export', this.filters);
    }

    private writeXlsx(lots: any[]) {
        const sheet: XlsxSheet = {
            name: 'Stocuri',
            freezeRows: 1,
            columns: [
                { width: 12 },
                { width: 40 },
                { width: 25 },
                { width: 18 },
                { width: 25 },
                { width: 30 },
                { width: 16 },
                { width: 18 },
                { width: 22 },
                { width: 10 },
            ],
            rows: [
                [
                    { value: 'Cod', variant: 'header' },
                    { value: 'Lot', variant: 'header' },
                    { value: 'Autor', variant: 'header' },
                    { value: 'Contract', variant: 'header' },
                    { value: 'Deponent', variant: 'header' },
                    { value: 'Licitatie', variant: 'header' },
                    { value: 'Stoc', variant: 'header' },
                    { value: 'Modificat la', variant: 'header' },
                    { value: 'Modificat de', variant: 'header' },
                    { value: 'Vandut', variant: 'header' },
                ],
            ],
        };

        for (const lot of lots) {
            sheet.rows.push([
                Stock.formatSku(lot.sku),
                lot.name || '',
                (lot.author && lot.author.name) || '',
                Stock.formatContractNumber(lot.contract),
                depositorName(lot.depositor),
                (lot.auctions || [])
                    .map((auction: any) => auction.name)
                    .join(', '),
                Stock.stockLabel(lot),
                this.formatDate(lot.stockChangedAt),
                this.userName(lot.stockChangedBy),
                lot.sold ? 'Da' : 'Nu',
            ]);
        }

        saveXlsx(
            [sheet],
            `stocuri-${moment().format('YYYY-MM-DD')}.xlsx`,
        );
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/stock/list/index.ts